import { useState, useEffect, useContext } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useTicketCounts = ({ selectedQueueIds, showAll, isRefreshing }) => {
  const { user } = useContext(AuthContext);
  const [ticketCounts, setTicketCounts] = useState({
    open: 0,
    pending: 0,
    group: 0,
    closed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = user?.profile === "admin";
  const canSeeAllTickets = user?.allTicket === "enabled";
  const canSeeAllUserChats = user?.allUserChat === "enabled";
  const canAccessGroups = user?.allowGroup === true;
  const canSeeAllHistory = user?.allHistoric === "enabled";

  const effectiveShowAll =
    showAll &&
    (isAdmin || canSeeAllTickets || canSeeAllHistory || canSeeAllUserChats);

  const shouldIgnoreQueueFilter = effectiveShowAll;

  useEffect(() => {
    let isMounted = true;
    const fetchTicketCounts = async () => {
      if (!isMounted) return;

      setLoading(true);
      setError(null);

      try {
        const validQueueIds = Array.isArray(selectedQueueIds)
          ? selectedQueueIds
          : [];

        let filteredQueueIds = validQueueIds;
        if (
          !isAdmin &&
          canSeeAllUserChats &&
          !canSeeAllHistory &&
          user?.queues
        ) {
          const userQueueIds = user.queues.map((q) => q.id);
          filteredQueueIds = validQueueIds.filter((id) =>
            userQueueIds.includes(id)
          );
        }

        const { data } = await api.get("/tickets/count", {
          params: {
            queueIds: JSON.stringify(filteredQueueIds),
            showAll: showAll ? "true" : "false",
          },
          timeout: 20000,
        });

        if (isMounted) {
          console.log("[useTicketCounts] API Response:", data);
          if (data && typeof data === "object") {
            const normalizedData = {
              open: parseInt(data.open) || 0,
              pending: parseInt(data.pending) || 0,
              group: parseInt(data.group) || 0,
              closed: parseInt(data.closed) || 0,
            };

            console.log("[useTicketCounts] Normalized counts:", normalizedData);

            if (!canAccessGroups) {
              normalizedData.group = 0;
            }

            setTicketCounts(normalizedData);
          } else {
            console.error("[useTicketCounts] Invalid data format:", data);
            setError(new Error("Formato de dados inválido"));
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTicketCounts();

    return () => {
      isMounted = false;
    };
  }, [
    selectedQueueIds,
    showAll,
    isRefreshing,
    canAccessGroups,
    effectiveShowAll,
    isAdmin,
    canSeeAllUserChats,
    canSeeAllHistory,
    user,
  ]);

  return {
    ticketCounts,
    loading,
    error,
    permissions: {
      isAdmin,
      canSeeAllTickets,
      canSeeAllUserChats,
      canAccessGroups,
      canSeeAllHistory,
      effectiveShowAll,
      shouldIgnoreQueueFilter,
    },
  };
};

export default useTicketCounts;

